import { captureException } from '@sentry/react';
import { UnreachableError } from 'base/helpers';
import { globalResizeObserver } from 'base/ui/measure';
import { isMac } from 'blocks/platform';
import { comparer, computed, makeObservable, observable, reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { computedFn } from 'mobx-utils';
import React from 'react';
import {
  isInputElement,
  shortcut,
  useKeyboardShortcuts,
} from '../keyboard_shortcuts';
import styles from './canvas.css';
import { invertZoomDirectionPreference } from './preferences_menu';
import { pageDndDomain } from './sitemap/page_dnd';
import { sectionDndDomain } from './sitemap/section_dnd';
import { spaceDndDomain } from './space_selector';

const MAX_ZOOM_POWER = Math.log2(9.99); // max of 999%
const DRAG_MOVE_THRESHOLD_PX = 64;
const ENSURE_VISIBLE_PADDING_PX = 64;
const OVERSCROLL_PX = 5000;

export type ViewportState = {
  x: number;
  y: number;
  zoomPower: number;
};

export type EnsureVisibleOpts = {
  applyMinimumSize?: boolean;
  panMode?: 'nearest' | 'center';
};

const DEFAULT_PAN_MODE = 'nearest' satisfies EnsureVisibleOpts['panMode'];

/**
 * Terms:
 *
 *      zoom = a scale factor
 *              zoom increases as things get bigger
 *      zoom power = log2(zoom)  (i.e. zoom = 2 ** zoom_power)
 *              It'd be nice to rewrite the whole thing to use powers only
 *      zoom 1 <-> zoom power = 0
 *              Layout happens at zoom 1 only
 */
export class CanvasViewport {
  @observable.ref
  private _zoomPower = 0;
  /* Canvas X scroll, at zoom = 1 */
  @observable.ref
  private _x = 0;
  /* Canvas Y scroll, at zoom = 1 */
  @observable.ref
  private _y = 0;

  get zoomPower() {
    return this._zoomPower;
  }
  set zoomPower(zoomPower: number) {
    this._zoomPower = protectFromNaN(zoomPower, 'zoomPower', this.zoomPower);
  }
  get x() {
    return this._x;
  }
  set x(x: number) {
    this._x = protectFromNaN(x, 'x', this.x);
  }
  get y() {
    return this._y;
  }
  set y(y: number) {
    this._y = protectFromNaN(y, 'y', this.y);
  }

  @observable.ref
  nextLayoutEffect: (() => void) | undefined = undefined;

  inner: HTMLElement | undefined = undefined;
  // for mouse -> canvas coordinates, not used in an observable context
  outer: HTMLElement | undefined = undefined;

  @observable.ref
  private animation: Animation | undefined = undefined;

  get transform() {
    return toTransform(this);
  }

  constructor(
    readonly didUpdateTransformOrSize: () => void,
    readonly enableCursorOptimization: boolean,
  ) {
    makeObservable(this);
  }

  private get innerWidth() {
    return this.inner?.offsetWidth ?? 0;
  }
  private get innerHeight() {
    return this.inner?.offsetHeight ?? 0;
  }
  private get outerWidth() {
    return this.outer?.offsetWidth ?? 0;
  }
  private get outerHeight() {
    return this.outer?.offsetHeight ?? 0;
  }

  @computed
  get zoom() {
    return 2 ** this.zoomPower;
  }

  private get minZoomPower() {
    // if the user has resized their window, let them keep their zoom:
    const heightRatio =
      this.outerHeight > 2 * ENSURE_VISIBLE_PADDING_PX &&
      this.innerHeight > -2 * OVERSCROLL_PX
        ? (this.outerHeight - 2 * ENSURE_VISIBLE_PADDING_PX) /
          (this.innerHeight + 2 * OVERSCROLL_PX)
        : -1;

    const widthRatio =
      this.outerWidth > 2 * ENSURE_VISIBLE_PADDING_PX &&
      this.innerWidth > -2 * OVERSCROLL_PX
        ? (this.outerWidth - 2 * ENSURE_VISIBLE_PADDING_PX) /
          (this.innerWidth + 2 * OVERSCROLL_PX)
        : -1;

    const heightZoom = heightRatio > 0 ? Math.log2(heightRatio) : -1;
    const widthZoom = widthRatio > 0 ? Math.log2(widthRatio) : -1;

    return Math.min(this.zoomPower, -1, heightZoom, widthZoom);
  }

  bind(outer: HTMLElement, inner: HTMLElement) {
    this.outer = outer;
    this.inner = inner;

    const disposers = [
      reaction(
        () => (this.animation == null ? this.transform : undefined),
        (transform) => {
          if (transform) {
            inner.style.transform = transform;
            this.didUpdateTransformOrSize();
            this.animationFrameTicker?.cancel();
          } else {
            this.animationFrameTicker?.start();
          }
        },
        { fireImmediately: true },
      ),
      globalResizeObserver.observe(this.inner, this.didUpdateTransformOrSize),
      this.mouseHandler.bind(outer),
      this.dragHandler.bind(outer),
    ];
    return () => disposers.forEach((d) => d());
  }

  get showActionOverlay() {
    if (this.mouseHandler.isSpaceDown) {
      return true;
    }
    if (this.mouseHandler.isPanning) {
      return true;
    }
    if (this.mouseHandler.isZDown) {
      return true;
    }
    return false;
  }

  get cursorOverlay() {
    if (this.mouseHandler.isZDown && this.mouseHandler.isAltDown) {
      return 'zoom-out';
    }
    if (this.mouseHandler.isZDown && !this.mouseHandler.isAltDown) {
      return 'zoom-in';
    }
    if (this.mouseHandler.isPanning) {
      return 'grabbing';
    }
    if (this.mouseHandler.isSpaceDown) {
      return 'grab';
    }
    return 'default';
  }

  private animationFrameTicker = this.didUpdateTransformOrSize
    ? new AnimationFrameTicker(this.didUpdateTransformOrSize)
    : undefined;

  /**
   * Apply scrolling limits, keeping zoom the same.
   *
   * The content is centered if it is smaller than the viewport.
   */
  panWithinLimits(): void {
    const { outerWidth, outerHeight, zoom } = this;
    const os = OVERSCROLL_PX;
    checkForNaNs('panWithinLimits', {
      outerWidth,
      outerHeight,
      zoom,
      innerWidth: this.innerWidth,
      innerHeight: this.innerHeight,
      x: this.x,
      y: this.y,
    });

    const innerWidth = this.innerWidth + os * 2;
    const centerX = (innerWidth - outerWidth / zoom) / 2;
    const minX = Math.min(0, centerX) - os;
    const maxX = Math.max(centerX * 2, minX + centerX) - os;

    const innerHeight = this.innerHeight + os * 2;
    const centerY = (innerHeight - outerHeight / zoom) / 2;
    const minY = Math.min(0, centerY) - os;
    const maxY = Math.max(centerY * 2, minY + centerY) - os;

    this.x = Math.max(minX, Math.min(this.x, maxX));
    this.y = Math.max(minY, Math.min(this.y, maxY));
  }

  save(): ViewportState {
    return {
      zoomPower: this.zoomPower,
      x: this.x,
      y: this.y,
    };
  }

  cancelAnimation() {
    const { animation } = this;
    this.animation = undefined;
    if (!animation) return;
    animation.commitStyles();
    animation.cancel();
    const state = fromTransform(this.inner?.style.transform ?? '');
    if (state) {
      this.x = state.x;
      this.y = state.y;
      this.zoomPower = state.zoomPower;
    }
  }

  restore(state: ViewportState) {
    this.cancelAnimation();
    checkForNaNs('restore', state);
    this.zoomPower = Math.max(
      this.minZoomPower,
      Math.min(state.zoomPower, MAX_ZOOM_POWER),
    );
    this.x = state.x;
    this.y = state.y;
    this.panWithinLimits();
  }

  /** Zooms in or out, snapping to a "neat" looking zoom level */
  readonly makeZoomDeltaSnappedCallback = computedFn((delta: number) => () => {
    const v = Math.round(this.zoomPower + delta);
    this.zoomAroundPoint(v, this.outerWidth / 2, this.outerHeight / 2);
  });

  readonly makeZoomSetCallback = computedFn((v: number) => () => {
    this.zoomAroundPoint(v, this.outerWidth / 2, this.outerHeight / 2);
  });

  readonly zoomToFit = () => {
    const PAD = { top: 128, bottom: 64, left: 128, right: 128 };
    const availableHeight = this.outerHeight - PAD.top - PAD.bottom;
    const availableWidth = this.outerWidth - PAD.left - PAD.right;

    // Zoom to fit:
    if (availableHeight > 0 && availableWidth > 0) {
      this.zoomPower = Math.min(
        Math.log2(availableHeight / this.innerHeight),
        Math.log2(availableWidth / this.innerWidth),
      );
    }
    // Center content horizontally:
    this.x = (this.innerWidth - this.outerWidth / this.zoom) / 2;
    // Top align content vertically:
    this.y = -PAD.top / this.zoom;
    checkForNaNs('zoomToFit', {
      zoomPower: this.zoomPower,
      x: this.x,
      y: this.y,
    });
  };

  /** The points should be relative to the `outer` element's client box */
  zoomAroundPoint(newZoomPower: number, screenX: number, screenY: number) {
    // Given to convert from screen space to z1 space:
    //   x@screen = (x@z1 - this.x) * zoom
    //   x@z1 = (x@screen / zoom) + this.x
    //
    // We are going from zA to zB.  We want to keep x@screen & x@z1 the same:
    //                        x@z1 = x@z1
    //  (x@screen / zA) + stateA.x = (x@screen / zB) + stateB.x
    //
    //    => stateB.x = (x@screen / zA) + stateA.x - (x@screen / zB)
    //
    // By "screen" I mean relative to the canvas, in screen space.
    const zA = this.zoom;
    this.zoomPower = Math.max(
      this.minZoomPower,
      Math.min(newZoomPower, MAX_ZOOM_POWER),
    );
    const zB = this.zoom;
    this.x = screenX / zA + this.x - screenX / zB;
    this.y = screenY / zA + this.y - screenY / zB;
    checkForNaNs('zoomAroundPoint', {
      zoomPower: this.zoomPower,
      x: this.x,
      y: this.y,
    });
  }

  /**
   * Pans the canvas so the cursor position is the same as a % of the content's size,
   * after making some state change.
   **/
  async maintainCursorPosition(setNewState: () => void) {
    const cursorX = this.mouseHandler.prevX / this.zoom;
    const cursorY = this.mouseHandler.prevY / this.zoom;
    const xf = (this.x + cursorX) / this.innerWidth;
    const yf = (this.y + cursorY) / this.innerHeight;
    setNewState();

    let runCallback = false;
    const afterReactRender = () => {
      if (runCallback) return;
      runCallback = true;
      this.x = xf * this.innerWidth - cursorX;
      this.y = yf * this.innerHeight - cursorY;
      this.panWithinLimits();
    };
    // useLayoutEffect runs earlier, but I don't know if it always works
    this.nextLayoutEffect = afterReactRender;
    setTimeout(afterReactRender, 0);
  }

  private async animateTo(setNewState: () => void) {
    this.cancelAnimation();
    const { zoomPower: originalZoomPower, x: originalX, y: originalY } = this;
    this.animation?.cancel();
    this.animation = undefined;

    let animation;
    try {
      setNewState();
      // Run the animation
      const dZoom = Math.abs(this.zoomPower - originalZoomPower) / 2;
      const dPosition =
        Math.hypot(this.x - originalX, this.y - originalY) /
        Math.max(this.outerWidth, this.outerHeight);
      const duration = Math.min(Math.max(dZoom, dPosition) * 500, 1000);
      this.animation = animation = this.inner?.animate(
        calculateTransitionFrames(
          { x: originalX, y: originalY, zoomPower: originalZoomPower },
          this,
          { width: this.outerWidth, height: this.outerHeight },
        ),
        { duration, easing: 'ease' },
      );
      // .finished throws if the animation is cancelled.
      await animation?.finished.catch(() => {});
    } finally {
      if (this.animation === animation) {
        this.animation = undefined;
      }
    }
  }

  /** Pans and zooms the canvas so that the element is fully visible */
  ensureVisible(el: HTMLElement, opts: EnsureVisibleOpts = {}) {
    if (!this.outer?.contains(el)) {
      throw new Error('Element is not a descendant of the canvas');
    }
    // in screen space (client*)
    const elScreenSpace = el.getBoundingClientRect();
    const innerScreenSpace = this.inner!.getBoundingClientRect();
    const transformerToScreen =
      innerScreenSpace.width / this.inner!.offsetWidth;
    if (Math.abs(transformerToScreen - this.zoom) > 0.00001) {
      // This means one of our invariants have gone totally wrong,
      // Screen space should be this.x * this.zoom
      console.warn(
        'ensureVisible: transformerToScreen !== this.zoom',
        transformerToScreen,
        this.zoom,
      );
    }
    // relative to this.inner's coordinate system ("canvas space")
    const width = elScreenSpace.width / this.zoom;
    const height = elScreenSpace.height / this.zoom;
    const left = (elScreenSpace.left - innerScreenSpace.left) / this.zoom;
    const top = (elScreenSpace.top - innerScreenSpace.top) / this.zoom;

    this.animateTo(() => {
      // Zoom
      const zoomToFill = Math.min(
        (this.outerWidth - 2 * ENSURE_VISIBLE_PADDING_PX) / width,
        (this.outerHeight - 2 * ENSURE_VISIBLE_PADDING_PX) / height,
      );
      const newZoom = opts.applyMinimumSize
        ? Math.max(zoomToFill * 0.8, this.zoom)
        : Math.min(zoomToFill, this.zoom);
      const newZoomPower = newZoom > 0 ? Math.log2(newZoom) : 0;
      this.zoomAroundPoint(
        newZoomPower,
        (elScreenSpace.left + elScreenSpace.right) / 2 - this.outer!.clientLeft,
        (elScreenSpace.top + elScreenSpace.bottom) / 2 - this.outer!.clientTop,
      );

      // Pan
      const panMode = opts.panMode ?? DEFAULT_PAN_MODE;
      if (panMode === 'nearest') {
        const pad = ENSURE_VISIBLE_PADDING_PX / newZoom;
        const leftmostX = left - pad;
        const rightmostX = left + width - this.outerWidth / newZoom + pad;
        this.x = Math.max(rightmostX, Math.min(this.x, leftmostX));
        const topmostY = top - pad;
        const bottommostY = top + height - this.outerHeight / newZoom + pad;
        this.y = Math.max(bottommostY, Math.min(this.y, topmostY));
      } else if (panMode === 'center') {
        this.x = left + width / 2 - this.outerWidth / newZoom / 2;
        this.y = top + height / 2 - this.outerHeight / newZoom / 2;
      } else {
        throw new UnreachableError(panMode);
      }

      this.panWithinLimits();
    });
  }

  private readonly mouseHandler = new MouseGestureHandler(this);
  private readonly dragHandler = new DragGestureHandler(this);
  private readonly touchHandler = new TouchGestureHandler(this);

  readonly outerEventHandlers: React.HTMLAttributes<HTMLDivElement> = {
    onPointerDown: this.touchHandler.handlePointerDownAndMove,
    onPointerMove: this.touchHandler.handlePointerDownAndMove,
    onPointerUp: this.touchHandler.handlePointerUp,
    onPointerCancel: this.touchHandler.handlePointerUp,
    onPointerLeave: this.touchHandler.handlePointerUp,
    onPointerOut: this.touchHandler.handlePointerUp,
  };
}

export function toTransform({ x, y, zoomPower }: ViewportState) {
  return `scale(${2 ** zoomPower}) translate(${-x}px, ${-y}px)`;
}

export function fromTransform(transform: string): ViewportState | undefined {
  const match = transform.match(
    /scale\((?<zoomPower>[\d.]+)\) translate\((?<x>[-\d.]+)px, (?<y>[-\d.]+)px\)/,
  );
  if (!match?.groups) {
    return undefined;
  }
  const { x, y, zoomPower } = match.groups;
  return {
    x: -protectFromNaN(parseFloat(x), 'x', 0),
    y: -protectFromNaN(parseFloat(y), 'y', 0),
    zoomPower: protectFromNaN(Math.log2(parseFloat(zoomPower)), 'zoomPower', 0),
  };
}

function protectFromNaN(
  newValue: number,
  valueName: string,
  defaultValue: number,
) {
  if (Number.isNaN(newValue) || typeof newValue !== 'number') {
    // If we throw an error that'd crash the app, so we log it instead.
    captureException(
      new Error(`Canvas invalid ${valueName} is not number: ${newValue}`),
    );
    return defaultValue;
  }
  return newValue;
}

function checkForNaNs(functionName: string, numbers: Record<string, number>) {
  const nanKeys = Object.entries(numbers)
    .filter(([, n]) => Number.isNaN(n))
    .map(([key]) => key);
  if (nanKeys.length > 0) {
    throw new Error(
      `${functionName}: ${nanKeys.join(', ')} are NaN. ${JSON.stringify(
        numbers,
      )}`,
    );
  }
}

function calculateTransitionFrames(
  from: ViewportState,
  to: ViewportState,
  view: { width: number; height: number },
) {
  // If we just transitioned over x&y, we'd be fixing the top left corner
  // of the canvas. Instead, we want to fix the center of the canvas.
  const centerOffsetX = view.width / 2;
  const centerOffsetY = view.height / 2;
  const numFrames = 20;
  const fromZoom = 2 ** from.zoomPower;
  const fromCenterX = from.x + centerOffsetX / fromZoom;
  const fromCenterY = from.y + centerOffsetY / fromZoom;
  const toZoom = 2 ** to.zoomPower;
  const toCenterX = to.x + centerOffsetX / toZoom;
  const toCenterY = to.y + centerOffsetY / toZoom;
  return Array.from({ length: numFrames }, (_, i) => {
    const t = i / (numFrames - 1);
    const zoomPower = (1 - t) * from.zoomPower + t * to.zoomPower;
    const centerX = (1 - t) * fromCenterX + t * toCenterX;
    const centerY = (1 - t) * fromCenterY + t * toCenterY;
    const zoom = 2 ** zoomPower;
    return {
      offset: t,
      transform: toTransform({
        zoomPower,
        x: centerX - centerOffsetX / zoom,
        y: centerY - centerOffsetY / zoom,
      }),
    };
  });
}

class AnimationFrameTicker {
  private id: number | undefined = undefined;

  constructor(private readonly callback: () => void) {}

  private readonly onFrame = () => {
    this.callback();
    this.id = requestAnimationFrame(this.onFrame);
  };

  start() {
    if (this.id != null) return;
    this.id = requestAnimationFrame(this.onFrame);
  }

  cancel() {
    if (this.id == null) return;
    cancelAnimationFrame(this.id);
    this.id = undefined;
  }
}

const ZOOM_RATE_TOUCHPAD = 1 / 150;
const ZOOM_RATE_MOUSE = 1 / 400;

class MouseGestureHandler {
  constructor(private readonly viewport: CanvasViewport) {
    makeObservable(this, {
      isSpaceDown: observable,
      isPanning: observable,
      isZDown: observable,
      isAltDown: observable,
    });
  }

  prevX: number = 0;
  prevY: number = 0;
  isPanning = false;
  // Ignores if space is down inside a text input
  isSpaceDown = false;
  isZDown = false;
  isAltDown = false;
  private isCtrlDown = false;
  private isMetaDown = false;

  private get isCtrlOrMetaDown() {
    return this.isCtrlDown || this.isMetaDown;
  }

  bind(el: HTMLElement) {
    // React synthetic wheel can't be prevented.  So we bind an event listener manually:
    window.addEventListener('wheel', this.handleWheelOutside, {
      passive: false,
    });
    el.addEventListener('wheel', this.handleWheel);
    el.addEventListener('mousedown', this.handleMouseDown);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('blur', this.handleWindowBlur);
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
    return () => {
      window.removeEventListener('wheel', this.handleWheelOutside);
      el.removeEventListener('wheel', this.handleWheel);
      el.removeEventListener('mousedown', this.handleMouseDown);
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.handleMouseUp);
      window.removeEventListener('blur', this.handleWindowBlur);
      document.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('keyup', this.handleKeyUp);
    };
  }

  private updateCursor() {
    if (this.viewport.enableCursorOptimization) {
      return;
    }
    // Prefer to directly update cursor on body instead of setting
    // cursor on a div and showing the div on screen. The latter tends to
    // occasionally bug out on chrome and not update correctly.
    if (this.isZDown && this.isAltDown) {
      document.body.style.cursor = 'zoom-out';
    } else if (this.isZDown && !this.isAltDown) {
      document.body.style.cursor = 'zoom-in';
    } else if (this.isPanning) {
      document.body.style.cursor = 'grabbing';
    } else if (this.isSpaceDown) {
      document.body.style.cursor = 'grab';
    } else {
      document.body.style.cursor = 'default';
    }
  }

  private readonly handleWheelOutside = (e: WheelEvent) => {
    if (e.ctrlKey || this.isCtrlOrMetaDown) {
      // pinching outside the canvas is probably a mistake.  Prevent it
      e.preventDefault();
    }
  };

  private readonly handleWheel = (e: WheelEvent) => {
    const el = e.currentTarget as HTMLElement;
    e.preventDefault();
    e.stopPropagation();
    // e.ctrlKey is always false in Safari (wtf safari)
    if (e.ctrlKey || this.isCtrlOrMetaDown) {
      const zoomRateMouse =
        (invertZoomDirectionPreference.get() ? -1 : 1) * ZOOM_RATE_MOUSE;
      // e.ctrlKey is true either if:
      //   - the user is using a touchpad and is pinching
      //   - the user actually has the control key down
      // So we check against keypress events to distinguish the two.
      const rate = this.isCtrlOrMetaDown ? zoomRateMouse : ZOOM_RATE_TOUCHPAD;
      this.viewport.zoomAroundPoint(
        this.viewport.zoomPower - e.deltaY * rate,
        e.clientX - el.clientLeft,
        e.clientY - el.clientTop,
      );
    } else {
      checkForNaNs('handleWheel_pan', {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
      });
      const [dx, dy] =
        e.shiftKey && !isMac ? [e.deltaY, e.deltaX] : [e.deltaX, e.deltaY];
      if (Math.abs(dx) + Math.abs(dy) > 1) {
        this.viewport.cancelAnimation();
      }
      this.viewport.x += dx / this.viewport.zoom;
      this.viewport.y += dy / this.viewport.zoom;
    }
    this.viewport.panWithinLimits();
  };

  private readonly handleMouseDown = (e: MouseEvent) => {
    this.prevX = e.clientX;
    this.prevY = e.clientY;
    // Space left click, or middle mouse button
    this.isPanning = (this.isSpaceDown && e.button === 0) || e.button === 1;
    if (this.isPanning) {
      e.preventDefault();
    }
    // Holding down z and clicking should trigger a zoom
    if (this.isZDown) {
      const delta = this.isAltDown ? -1 : 1;
      const v = Math.round(this.viewport.zoomPower + delta);
      this.viewport.zoomAroundPoint(v, e.clientX, e.clientY);
    }

    this.updateCursor();
  };

  private readonly handleMouseMove = (e: MouseEvent) => {
    if (e.buttons === 0 && this.isPanning) {
      // mouseup outside of the window
      this.isPanning = false;
    }
    if (this.isPanning && this.prevX != 0 && this.prevY != 0) {
      e.preventDefault();
      // we can't use movementX as it is a totally unit safari vs chrome
      const dx = e.clientX - this.prevX;
      const dy = e.clientY - this.prevY;
      this.viewport.x += -dx / this.viewport.zoom;
      this.viewport.y += -dy / this.viewport.zoom;
      this.viewport.panWithinLimits();
    }
    this.prevX = e.clientX;
    this.prevY = e.clientY;

    this.updateCursor();
  };

  private readonly handleMouseUp = (e: MouseEvent) => {
    this.prevX = e.clientX;
    this.prevY = e.clientY;
    if (this.isPanning && e.button === 1) {
      // Middle mouse button.  Can't use `buttons` because none are pressed now.
      // prevent pasting into text boxes after middle button panning
      e.preventDefault();
    }
    this.isPanning = false;
  };

  private readonly handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Control') {
      this.isCtrlDown = true;
      // So that safari will notify us about the keyup:
      e.preventDefault();
    }
    if (e.key === 'Meta') {
      this.isMetaDown = true;
      // So that safari will notify us about the keyup:
      e.preventDefault();
    }
    if (e.key === 'Alt') {
      this.isAltDown = true;
    }
    if (e.key === ' ' && !isInputElement(e.target)) {
      this.isSpaceDown = true;
    }
    // Note we check code instead of key as
    // alt+z has a key of "Ω", code however reliabily tells us what the user
    // pressed on their keyboard.
    if (
      e.code === 'KeyZ' &&
      !isInputElement(e.target) &&
      !this.isCtrlOrMetaDown
    ) {
      this.isZDown = true;
    }

    this.updateCursor();
  };

  private readonly handleKeyUp = (e: KeyboardEvent) => {
    this.isCtrlDown = e.ctrlKey && e.key !== 'Control';
    this.isMetaDown = e.metaKey && e.key !== 'Meta';
    this.isSpaceDown = this.isSpaceDown && e.key !== ' ';
    this.isZDown = this.isZDown && e.code !== 'KeyZ';
    this.isAltDown = this.isAltDown && e.key !== 'Alt';

    this.updateCursor();
  };

  // We lose any keyup events after the window looses focus, so we assume all
  // keys are released.
  private readonly handleWindowBlur = () => {
    this.isCtrlDown = false;
    this.isMetaDown = false;
    this.isSpaceDown = false;
    this.isPanning = false;
    this.isZDown = false;
    this.isAltDown = false;
  };
}

class TouchGestureHandler {
  constructor(private readonly viewport: CanvasViewport) {}

  private touchs = new Map<number, { x: number; y: number }>();
  /* Maximum touches seen during this gesture */
  private maxTouches = 0;
  private panner = new KineticPanner(this.viewport);

  private get touchCenter() {
    let x = 0;
    let y = 0;
    for (const t of this.touchs.values()) {
      x += t.x;
      y += t.y;
    }
    const n = this.touchs.size;
    return { x: x / n, y: y / n };
  }

  private get touchDistance() {
    if (this.touchs.size !== 2) return 0;
    const [a, b] = this.touchs.values();
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  readonly handlePointerDownAndMove = (e: React.PointerEvent) => {
    const { pointerId, clientX, clientY } = e;
    if (e.pointerType !== 'touch') return;
    e.preventDefault();
    const isStarting = this.touchs.size === 0;

    const prevCenter = this.touchCenter;
    const prevDistance = this.touchDistance;
    this.touchs.set(pointerId, { x: clientX, y: clientY });
    const newCenter = this.touchCenter;
    const newDistance = this.touchDistance;
    const numTouches = this.touchs.size;
    this.maxTouches = Math.max(this.maxTouches, numTouches);

    if (e.type === 'pointerdown' && isStarting) {
      this.panner.onGestureStart();
    }
    if (e.type === 'pointermove') {
      this.viewport.x += (prevCenter.x - newCenter.x) / this.viewport.zoom;
      this.viewport.y += (prevCenter.y - newCenter.y) / this.viewport.zoom;
      this.viewport.panWithinLimits();
      if (numTouches === 2) {
        const newZoomPower = Math.log2(
          this.viewport.zoom * (newDistance / prevDistance),
        );
        this.viewport.zoomAroundPoint(newZoomPower, newCenter.x, newCenter.y);
      }
    }
  };

  readonly handlePointerUp = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    this.touchs.delete(e.pointerId);
    const numTouches = this.touchs.size;
    if (e.type === 'pointerup' && numTouches === 0) {
      if (this.maxTouches === 1) {
        this.panner.onGestureEnd();
      } else {
        // don't do kinetic panning if we were zooming
        this.panner.cancel();
      }
      this.maxTouches = 0;
      return;
    }
  };
}

const KINETIC_TIME_CONSTANT = 300;
const KINETIC_AMPLITUDE = 0.05;

class KineticPanner {
  constructor(private readonly viewport: CanvasViewport) {}

  private animationId: number | undefined;
  private trackingId: number | undefined;
  // velocity
  private vx = 0;
  private vy = 0;

  cancel() {
    this.animationId && cancelAnimationFrame(this.animationId);
    this.trackingId && cancelAnimationFrame(this.trackingId);
  }

  onGestureStart() {
    this.cancel();
    let lastX = this.viewport.x;
    let lastY = this.viewport.y;
    this.vx = this.vy = 0;
    let lastTimestamp = performance.now();
    const track = () => {
      const now = performance.now();
      const dt = now - lastTimestamp;
      const dx = this.viewport.x - lastX;
      const dy = this.viewport.y - lastY;
      lastTimestamp = now;
      lastX = this.viewport.x;
      lastY = this.viewport.y;
      // moving average
      this.vx = 0.8 * dx * dt + 0.2 * this.vx;
      this.vy = 0.8 * dy * dt + 0.2 * this.vy;
      this.trackingId = requestAnimationFrame(track);
    };
    this.trackingId = requestAnimationFrame(track);
  }

  onGestureEnd() {
    this.cancel();
    const { vx, vy } = this;
    const ax = KINETIC_AMPLITUDE * vx;
    const ay = KINETIC_AMPLITUDE * vy;
    const gestureEndTime = performance.now();
    const animate = () => {
      const elapsed = performance.now() - gestureEndTime;
      const dx = ax * Math.exp(-elapsed / KINETIC_TIME_CONSTANT);
      const dy = ay * Math.exp(-elapsed / KINETIC_TIME_CONSTANT);
      this.viewport.x += dx;
      this.viewport.y += dy;
      this.viewport.panWithinLimits();

      const moving = Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5;
      if (!moving) return;
      this.animationId = requestAnimationFrame(animate);
    };
    animate();
  }
}

const ALL_DND_DOMAINS = [sectionDndDomain, pageDndDomain, spaceDndDomain];

class DragGestureHandler {
  constructor(private readonly viewport: CanvasViewport) {}

  bind(el: HTMLElement) {
    return reaction(
      () => {
        for (const domain of ALL_DND_DOMAINS) {
          const cursor = domain.getCursorPosition();
          if (!cursor) continue;
          const box = el.getBoundingClientRect();
          const speed = 2 / DRAG_MOVE_THRESHOLD_PX;
          const dx =
            (DRAG_MOVE_THRESHOLD_PX -
              Math.min(
                cursor.x - box.left,
                box.right - cursor.x,
                DRAG_MOVE_THRESHOLD_PX,
              )) *
            speed *
            (cursor.x < (box.left + box.right) / 2 ? -1 : 1);
          const dy =
            (DRAG_MOVE_THRESHOLD_PX -
              Math.min(
                cursor.y - box.top,
                box.bottom - cursor.y,
                DRAG_MOVE_THRESHOLD_PX,
              )) *
            speed *
            (cursor.y < (box.top + box.bottom) / 2 ? -1 : 1);
          return [dx, dy];
        }
        return [0, 0];
      },
      ([dx, dy]) => this.panner.setDirection(dx, dy),
      { equals: comparer.structural },
    );
  }

  private readonly panner = new ConstantRatePanner(this.viewport);
}

function CursorOverlay({
  cursor,
}: {
  cursor: 'grab' | 'grabbing' | 'zoom-in' | 'zoom-out';
}) {
  return <div className={styles.cursorOverlay} style={{ cursor }} />;
}

class ConstantRatePanner {
  constructor(private readonly viewport: CanvasViewport) {}

  private readonly direction = { x: 0, y: 0 };
  private lastFrameTime: number | undefined = undefined;

  setDirection(x: number, y: number) {
    this.direction.x = x;
    this.direction.y = y;
    if (this.lastFrameTime != null || (x === 0 && y === 0)) return;
    this.lastFrameTime = performance.now();
    requestAnimationFrame(this.handleDragFrame);
  }

  private readonly handleDragFrame = () => {
    const { x: dx, y: dy } = this.direction;
    if (!dx && !dy) {
      this.lastFrameTime = undefined;
      return;
    }
    requestAnimationFrame(this.handleDragFrame);
    const now = performance.now();
    const deltaSecs = (now - (this.lastFrameTime ?? now)) / 1000;
    this.lastFrameTime = now;
    const speedPxPerSec = 250;
    const movement = (speedPxPerSec * deltaSecs) / this.viewport.zoom;
    this.viewport.x += dx * movement;
    this.viewport.y += dy * movement;
    // apply limits:
    this.viewport.panWithinLimits();
  };
}

export const Canvas = observer(
  ({
    viewport,
    children,
    unscaledOverlays,
    canvasRef,
    minHeight,
  }: {
    viewport: CanvasViewport;
    children: React.ReactNode;
    unscaledOverlays?: React.ReactNode;
    canvasRef?: React.MutableRefObject<HTMLDivElement | null>;
    minHeight?: string;
  }) => {
    const outerRef = React.useRef<HTMLDivElement>(null);
    const innerRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      if (!outerRef.current || !innerRef.current) {
        throw new Error('missing refs');
      }
      return viewport.bind(outerRef.current, innerRef.current);
    }, [viewport]);

    React.useLayoutEffect(() => {
      if (!viewport.nextLayoutEffect) return;
      viewport.nextLayoutEffect();
      viewport.nextLayoutEffect = undefined;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewport.nextLayoutEffect]);

    React.useEffect(() => {
      if (canvasRef) {
        canvasRef.current = outerRef.current;
      }
    }, [canvasRef]);

    const cursor = viewport.cursorOverlay;

    return (
      <div
        className={styles.canvas}
        onScroll={onCanvasScroll}
        ref={outerRef}
        style={{ minHeight }}
        {...viewport.outerEventHandlers}
      >
        <div
          ref={innerRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            transformOrigin: '0 0',
            // transform set by event handler
          }}
        >
          {children}
        </div>
        {unscaledOverlays}
        {viewport.showActionOverlay && <div className={styles.actionOverlay} />}
        {cursor !== 'default' && <CursorOverlay cursor={cursor} />}
        <CanvasDebugInfo viewport={viewport} />
      </div>
    );
  },
);

function onCanvasScroll(e: React.UIEvent<HTMLDivElement>) {
  if (e.currentTarget !== e.target) return;
  // The browser's focus apis can scroll the canvas.
  // This interferes with our pan/zoom transforms, so we prevent it:
  e.currentTarget.scrollLeft = 0;
  e.currentTarget.scrollTop = 0;
}

const CanvasDebugInfo = observer(function _CanvasDebugInfo({
  viewport,
}: {
  viewport: CanvasViewport;
}) {
  const [show, setShow] = React.useState(false);
  const key = shortcut('d', { cmd: true, shift: true, alt: true });
  useKeyboardShortcuts(true, {
    [key]: () => setShow((s) => !s),
  });

  if (!show) return null;
  const { zoom, zoomPower, x, y } = viewport;
  return (
    <div className={styles.debugInfo}>
      <div>
        zoom {zoom.toFixed(2)} (2<sup>{zoomPower.toFixed(2)}</sup>)
      </div>
      <div>x {x.toFixed(2)}</div>
      <div>y {y.toFixed(2)}</div>
      <i>hide: cmd+shift+alt+d</i>
    </div>
  );
});

export function useDisableSwipeBack() {
  return React.useEffect(() => {
    const els = [...document.querySelectorAll('html, body')];
    els.forEach((el) => el.classList.add(styles.disableSwipeBack));
    return () => {
      els.forEach((el) => el.classList.remove(styles.disableSwipeBack));
    };
  }, []);
}
